import { Country } from '@afterpay/types'
import { useFlag } from '@afterpaytouch/feature'
import { useEffect, useRef, useState } from 'react'
import { Features, IdentityDocument } from '../model'

type EffectCallback = () => void | (() => void | undefined)
type DependencyList = readonly any[]

interface UseSet<Type> {
  add: (items: Type[]) => void
  remove: (items: Type[]) => void
}

export function useSet<Type>(initialState: Type[]): [Set<Type>, UseSet<Type>] {
  const [state, setState] = useState<Set<Type>>(new Set(initialState))
  const add = (items: Type[]): void => setState((prev) => new Set([...Array.from(prev), ...items]))
  const remove = (items: Type[]): void => setState((prev) => new Set(Array.from(prev).filter((m) => !items.includes(m))))

  return [state, { add, remove }]
}

/**
 * Skips applying an effect from useEffect upon initial render
 */
export const useNonInitialEffect = (effect: EffectCallback, deps?: DependencyList): void => {
  const initialRender = useRef(true)
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false
    } else {
      return effect()
    }
  }, deps)
}

const DEFAULT_DOCUMENTS: IdentityDocument[] = ['licence', 'passport']
const DEFAULT_ALLOWED_DOCUMENTS = {
  [Country.AU]: ['licence', 'medicare', 'passport'],
}

export const useAllowedDocumentsByCountry = (country: Country): IdentityDocument[] => {
  const allowedDocuments = useFlag(Features.ALLOWED_DOCUMENTS_BY_COUNTRY, DEFAULT_ALLOWED_DOCUMENTS)

  return allowedDocuments[country] || DEFAULT_ALLOWED_DOCUMENTS[country] || DEFAULT_DOCUMENTS
}

type ControlledState<T> = [value: T, setValue: (value: T) => void]

/**
 * A React hook that synchronizes local state with a controlled prop value.
 *
 * Useful for components that can be both controlled and uncontrolled.
 * - Initializes state with the given value.
 * - Updates local state whenever the value prop changes.
 *
 * @param value The controlled value to sync with local state.
 * @returns A tuple containing the current state and a setter function.
 */
export const useControlledState = <T,>(value: T): ControlledState<T> => {
  const [state, setState] = useState(value)
  useEffect(() => {
    setState(value)
  }, [value])

  return [state, setState]
}
